/**
 * Per-instance attachment section keys for child-table repeatables (#1154 Bug 5).
 *
 * Convention: `{baseSectionId}.unit.{configurationId}`
 * Example: `small-ups.requirements.unit.42`
 */

import { SECTION_IDS } from '../constants/sectionIds';

import type { SelectedFile } from '../components/forms/FileUpload';
import type { SavedAttachment } from '../services/attachmentService';

/** Base attachment section ids scoped per repeatable instance in edit forms. */
export const SMALL_UPS_REPEATABLE_ATTACHMENT_BASE_SECTIONS = [
  SECTION_IDS.SMALL_UPS_REQUIREMENTS,
  SECTION_IDS.SMALL_UPS_ADDITIONAL,
] as const;

export const COOLING_SYSTEM_REPEATABLE_ATTACHMENT_BASE_SECTIONS = [
  SECTION_IDS.COOLING_READINESS_ADVANCED,
  // `_ADDITIONAL` last: the per-unit Additional-Info card is scoped through this
  // base (Bug 1 / Obs-7 fix), and the descriptor engine derives the additional
  // section id from the final entry.
  SECTION_IDS.COOLING_READINESS_ADDITIONAL,
] as const;

export const LIQUID_COOLING_CDU_REPEATABLE_ATTACHMENT_BASE_SECTIONS = [
  SECTION_IDS.LIQUID_COOLING_PROJECT,
  // `_NOTES` last (#997 B1 / PD-2): the per-CDU Notes (Additional-Info) card is
  // scoped through this base, matching the LU/PD/Cooling Obs-7 fix. Kept equal to
  // `LIQUID_COOLING_DESCRIPTOR.attachmentBaseSectionIds` (guard test #997 B3).
  SECTION_IDS.LIQUID_COOLING_NOTES,
] as const;

export const LARGE_UPS_REPEATABLE_ATTACHMENT_BASE_SECTIONS = [
  SECTION_IDS.FACILITY_POWER_LARGE_UPS,
  // `_ADDITIONAL` last (Bug 1 / Obs-7): per-unit Additional-Info attachments.
  SECTION_IDS.FACILITY_POWER_LARGE_UPS_ADDITIONAL,
] as const;

export const POWER_DISTRIBUTION_REPEATABLE_ATTACHMENT_BASE_SECTIONS = [
  SECTION_IDS.FACILITY_POWER_DISTRIBUTION,
  // `_ADDITIONAL` last (Bug 1 / Obs-7): per-unit Additional-Info attachments.
  SECTION_IDS.FACILITY_POWER_DISTRIBUTION_ADDITIONAL,
] as const;

export const RACK_REPEATABLE_ATTACHMENT_BASE_SECTIONS = [
  SECTION_IDS.RACK_CONFIGURATIONS,
  SECTION_IDS.RACK_POWER_INFO,
  SECTION_IDS.RACKS_ADDITIONAL,
] as const;

export const REPEATABLE_INSTANCE_ATTACHMENT_UNIT_SEGMENT = 'unit' as const;

export function buildRepeatableInstanceAttachmentSectionId(
  baseSectionId: string,
  configurationId: number,
): string {
  return `${baseSectionId}.${REPEATABLE_INSTANCE_ATTACHMENT_UNIT_SEGMENT}.${configurationId}`;
}

export function parseRepeatableInstanceAttachmentSectionId(
  sectionId: string,
): { baseSectionId: string; configurationId: number } | null {
  const marker = `.${REPEATABLE_INSTANCE_ATTACHMENT_UNIT_SEGMENT}.`;
  const index = sectionId.lastIndexOf(marker);
  if (index <= 0) {
    return null;
  }
  const baseSectionId = sectionId.slice(0, index);
  const idPart = sectionId.slice(index + marker.length);
  const configurationId = Number.parseInt(idPart, 10);
  if (!Number.isFinite(configurationId)) {
    return null;
  }
  return { baseSectionId, configurationId };
}

export function resolveRepeatableInstanceAttachmentBaseSectionId(
  sectionId: string,
): string {
  return (
    parseRepeatableInstanceAttachmentSectionId(sectionId)?.baseSectionId ??
    sectionId
  );
}

export function resolveRepeatableInstanceAttachmentSectionId(
  baseSectionId: string,
  configurationId: number | undefined,
): string {
  if (configurationId == null) {
    return baseSectionId;
  }
  return buildRepeatableInstanceAttachmentSectionId(
    baseSectionId,
    configurationId,
  );
}

export function selectAttachmentsForRepeatableInstance(
  attachmentsBySection: Record<string, SavedAttachment[]>,
  baseSectionId: string,
  configurationId: number | undefined,
): SavedAttachment[] {
  if (configurationId == null) {
    return attachmentsBySection[baseSectionId] ?? [];
  }
  const scopedId = buildRepeatableInstanceAttachmentSectionId(
    baseSectionId,
    configurationId,
  );
  return attachmentsBySection[scopedId] ?? [];
}

export function selectSelectedFilesForRepeatableInstance(
  selectedFiles: Record<string, SelectedFile[]>,
  baseSectionId: string,
  configurationId: number | undefined,
): SelectedFile[] {
  if (configurationId == null) {
    return selectedFiles[baseSectionId] ?? [];
  }
  const scopedId = buildRepeatableInstanceAttachmentSectionId(
    baseSectionId,
    configurationId,
  );
  return selectedFiles[scopedId] ?? [];
}

function remapUnitScopedSectionKeys<T>(
  sectionMap: Record<string, T[]>,
  idMap: ReadonlyMap<number, number>,
): Record<string, T[]> {
  if (idMap.size === 0) {
    return sectionMap;
  }
  const next: Record<string, T[]> = { ...sectionMap };
  for (const [fromId, toId] of idMap.entries()) {
    for (const [sectionId, items] of Object.entries(sectionMap)) {
      const parsed = parseRepeatableInstanceAttachmentSectionId(sectionId);
      if (!parsed || parsed.configurationId !== fromId) {
        continue;
      }
      const newSectionId = buildRepeatableInstanceAttachmentSectionId(
        parsed.baseSectionId,
        toId,
      );
      const merged = [...(next[newSectionId] ?? []), ...items];
      next[newSectionId] = merged;
      delete next[sectionId];
    }
  }
  return next;
}

/** Remap in-memory attachment maps when a draft configuration id is persisted. */
export function remapRepeatableInstanceAttachmentSections(
  attachmentsBySection: Record<string, SavedAttachment[]>,
  idMap: ReadonlyMap<number, number>,
): Record<string, SavedAttachment[]> {
  return remapUnitScopedSectionKeys(attachmentsBySection, idMap);
}

/**
 * Remap STAGED (not-yet-uploaded) file keys when draft unit ids are persisted
 * (#997 Obs 6). The section flush's server-side attachment remap only moves
 * rows that already exist in the DB; a staged upload keyed by
 * `base.unit.{tempId}` would upload into a dead bucket after the flush swaps
 * the unit to its real id. Apply this between the flush (which yields the
 * temp→real created-id map) and the file upload so files upload under
 * `base.unit.{realId}`.
 */
export function remapSelectedFilesForRepeatableInstances(
  selectedFiles: Record<string, SelectedFile[]>,
  idMap: ReadonlyMap<number, number>,
): Record<string, SelectedFile[]> {
  return remapUnitScopedSectionKeys(selectedFiles, idMap);
}

export function collectRepeatableInstanceAttachmentSectionRemaps(
  idMap: ReadonlyMap<number, number>,
  attachmentsBySection: Record<string, SavedAttachment[]>,
): Array<{
  fromSectionId: string;
  toSectionId: string;
  attachmentIds: number[];
}> {
  const remaps: Array<{
    fromSectionId: string;
    toSectionId: string;
    attachmentIds: number[];
  }> = [];
  for (const [fromId, toId] of idMap.entries()) {
    for (const [sectionId, attachments] of Object.entries(
      attachmentsBySection,
    )) {
      const parsed = parseRepeatableInstanceAttachmentSectionId(sectionId);
      if (
        !parsed ||
        parsed.configurationId !== fromId ||
        attachments.length === 0
      ) {
        continue;
      }
      remaps.push({
        fromSectionId: sectionId,
        toSectionId: buildRepeatableInstanceAttachmentSectionId(
          parsed.baseSectionId,
          toId,
        ),
        attachmentIds: attachments.map(item => item.id).filter(id => id > 0),
      });
    }
  }
  return remaps;
}

export function buildRepeatableInstanceAttachmentSectionRemapsForIdMap(
  idMap: ReadonlyMap<number, number>,
  baseSectionIds: readonly string[],
): Array<{ fromSection: string; toSection: string }> {
  const remaps: Array<{ fromSection: string; toSection: string }> = [];
  for (const [fromId, toId] of idMap.entries()) {
    for (const baseSectionId of baseSectionIds) {
      remaps.push({
        fromSection: buildRepeatableInstanceAttachmentSectionId(
          baseSectionId,
          fromId,
        ),
        toSection: buildRepeatableInstanceAttachmentSectionId(
          baseSectionId,
          toId,
        ),
      });
    }
  }
  return remaps;
}

/** Move in-memory pending uploads from step-level keys onto a draft unit id (#1154 Bug 5). */
export function moveSelectedFilesFromBaseToUnitScope(
  selectedFiles: Record<string, SelectedFile[]>,
  baseSectionIds: readonly string[],
  configurationId: number,
  onFilesSelected: (sectionId: string, files: SelectedFile[]) => void,
): void {
  for (const baseSectionId of baseSectionIds) {
    const baseFiles = selectedFiles[baseSectionId];
    if (!baseFiles?.length) {
      continue;
    }
    const scopedId = buildRepeatableInstanceAttachmentSectionId(
      baseSectionId,
      configurationId,
    );
    const existingScoped = selectedFiles[scopedId] ?? [];
    onFilesSelected(scopedId, [...existingScoped, ...baseFiles]);
    onFilesSelected(baseSectionId, []);
  }
}

export function resolveRepeatableDraftConfigurationId(
  draftId: number | undefined,
  fallbackTempId: number = -Date.now(),
): number {
  if (typeof draftId === 'number' && Number.isFinite(draftId) && draftId <= 0) {
    return draftId;
  }
  return fallbackTempId;
}
